import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, Observable, timer } from 'rxjs';

export interface ToastMessage {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  duration?: number;
  timestamp: Date;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private messagesSignal = signal<ToastMessage[]>([]);
  private messagesSubject = new BehaviorSubject<ToastMessage[]>([]);
  
  // Public observables
  messages$ = this.messagesSubject.asObservable();
  messages = this.messagesSignal.asReadonly();
  
  private defaultDuration = 5000; // 5 seconds
  
  constructor() {}
  
  /**
   * Show a success message
   */
  success(title: string, message: string, duration?: number): void {
    this.addMessage('success', title, message, duration);
  }
  
  /**
   * Show an error message
   */
  error(title: string, message: string, duration?: number): void {
    this.addMessage('error', title, message, duration || 7000); // Longer for errors
  }
  
  /**
   * Show a warning message
   */
  warning(title: string, message: string, duration?: number): void {
    this.addMessage('warning', title, message, duration);
  }
  
  /**
   * Show an info message
   */
  info(title: string, message: string, duration?: number): void {
    this.addMessage('info', title, message, duration);
  }
  
  /**
   * Add a message to the queue
   */
  private addMessage(
    type: ToastMessage['type'],
    title: string,
    message: string,
    duration?: number
  ): void {
    const id = this.generateId();
    const toastMessage: ToastMessage = {
      id,
      type,
      title,
      message,
      duration: duration ?? this.defaultDuration,
      timestamp: new Date()
    };
    
    const currentMessages = this.messagesSignal();
    const newMessages = [...currentMessages, toastMessage];
    
    this.messagesSignal.set(newMessages);
    this.messagesSubject.next(newMessages);
    
    // Auto-remove after duration
    if (toastMessage.duration > 0) {
      timer(toastMessage.duration).subscribe(() => {
        this.removeMessage(id);
      });
    }
  }
  
  /**
   * Remove a specific message
   */
  removeMessage(id: string): void {
    const currentMessages = this.messagesSignal();
    const filteredMessages = currentMessages.filter(msg => msg.id !== id);
    
    this.messagesSignal.set(filteredMessages);
    this.messagesSubject.next(filteredMessages);
  }
  
  /**
   * Clear all messages
   */
  clearAll(): void {
    this.messagesSignal.set([]);
    this.messagesSubject.next([]);
  }
  
  /**
   * Generate unique ID for messages
   */
  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }
  
  /**
   * Shorthand method for showing toast (compatible with old CommonService)
   */
  showToast(type: 'success' | 'error' | 'warning' | 'info', title: string, message: string): void {
    switch (type) {
      case 'success':
        this.success(title, message);
        break;
      case 'error':
        this.error(title, message);
        break;
      case 'warning':
        this.warning(title, message);
        break;
      case 'info':
        this.info(title, message);
        break;
    }
  }
}
